import type { DefaultChoice, RowChangedPayload } from "./runThrough"
import type { SectionDef, Stages } from "./engine"

/**
 * The decisions page's plain functions that read the same in both simulators. Every figure is one
 * the engine's records carry; nothing is computed afresh.
 */

const inr = new Intl.NumberFormat("en-IN")
export const rupees = (n: number) => `₹${inr.format(Math.round(n))}`

export const NUMBER_WORDS = ["No", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten"]

/** The stage the run is on: the first not landed, or the draft. */
export function currentStage(sections: SectionDef[], draftingNow: string, stages: Stages): { stage: string; now: string } {
  const next = sections.find((s) => !stages[s.stage])
  return next ? { stage: next.stage, now: next.now } : { stage: "drafting", now: draftingNow }
}

export function draftChecked(stages: Stages): { written: boolean; checked: boolean; notes: number; sections: number } {
  const rows = stages.drafting?.rows ?? []
  return {
    written: rows.length > 0,
    checked: rows.length > 0 && rows.every((r) => r.text !== "written"),
    notes: rows.filter((r) => r.text === "a note").length,
    sections: rows.length,
  }
}

/** "The draft is written and checked." and its earlier states, the verdict line's tail. */
export function draftTail(stages: Stages): string {
  const d = draftChecked(stages)
  return !d.written ? "The draft is being written." : d.checked ? "The draft is written and checked." : "The draft is written."
}

export function allChosen(sections: SectionDef[], stages: Stages): DefaultChoice[] {
  return sections.flatMap((s) => stages[s.stage]?.chosen ?? [])
}

/** The glance's last cell: the person's changes once there are any, else Nira's own defaults. */
export function choseCell(sections: SectionDef[], stages: Stages, changes: RowChangedPayload[]) {
  const chosen = allChosen(sections, stages)
  const mine = changes.filter((c) => c.by === "user")
  return mine.length
    ? { label: "You chose", value: `${mine.length} ${mine.length === 1 ? "change" : "changes"} · ${chosen.length} ${chosen.length === 1 ? "default" : "defaults"}`, sub: mine.map((c) => c.row_id).join(", ") }
    : { label: "Nira chose", value: `${chosen.length} ${chosen.length === 1 ? "default" : "defaults"}`, sub: chosen.map((c) => c.text).slice(0, 3).join(" · ") || "none" }
}

/** The person's change on this row, latest, if any. */
export const changeOf = (changes: RowChangedPayload[], stage: string, rowId: string) =>
  [...changes].reverse().find((c) => c.stage === stage && c.row_id === rowId && c.by === "user")

/** The engine's own value before the first change: what "Restore Nira's call" goes back to. */
export const originalOf = (changes: RowChangedPayload[], stage: string, rowId: string) =>
  changes.find((c) => c.stage === stage && c.row_id === rowId)?.from

export interface DraftProgress {
  written: number
  total: number
  complete: boolean
  ready: boolean
  label: string
}

/**
 * How far the draft has got. The draft is shown only when `ready`: every expected section has
 * text, or nothing is running or pending (finished, or idle after a refresh of a stopped draft; a partial
 * draft stays reachable). A running or awaiting_user draft is never ready until complete.
 */
export function draftProgress(
  draft: { sections: Record<string, { markdown: string } | undefined>; expected?: string[] | null } | null,
  status: string,
): DraftProgress {
  const sections = draft?.sections ?? {}
  const written = (id: string) => (sections[id]?.markdown ?? "").trim() !== ""
  const expected = draft?.expected ?? null
  const total = expected?.length ?? 0
  const count = expected && expected.length > 0
    ? expected.filter(written).length
    : Object.keys(sections).filter(written).length
  const complete = total > 0 && count === total
  const ready = count > 0 && (complete || status === "finished" || status === "idle")
  return { written: count, total, complete, ready, label: total === 0 ? "Drafting" : `Drafting — ${count} of ${total} sections` }
}
